import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Formik } from "formik";
import * as yup from "yup";

import {
  HvButton,
  HvDialog,
  HvDialogActions,
  HvDialogContent,
  HvDialogTitle,
} from "@hitachivantara/uikit-react-core";

import { IconData } from "lib/api";

import Location, { LocationFormData } from "./Location";

import useStyles from "./styles";

export interface SelectLocationDialogProps {
  isOpen: boolean;
  onSelection?: (data: LocationFormData) => void;
  onCancel?: () => void;
  location?: LocationFormData;
  marker?: IconData;
}

/**
 * Dialog form for editing a Category.
 *
 * While the use cases remain similar, it is shared with the edit of Subcategory,
 * by switching the type paramenter.
 *
 * @param isOpen If `true`, the Dialog is open.
 * @param onSelection Called for selecting the location.
 * @param onCancel Called when the dialog is closed.
 * @param initialValues The initial field values.
 */
const SelectLocationDialog: React.FC<SelectLocationDialogProps> = ({
  isOpen,
  onSelection,
  onCancel,
  location,
  marker,
}) => {
  const classes = useStyles();

  const { t } = useTranslation("selectLocationDialog");

  const onCloseHandler = (
    event: unknown,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason !== "backdropClick") {
      onCancel?.();
    }
  };

  const onCancelHandler = () => {
    onCancel?.();
  };

  const onSubmitHandler = (values: LocationFormData) => {
    onSelection?.(values);
  };

  const validationSchema = useMemo(
    () =>
      yup.object().shape(
        {
          address: yup.string().required(t("validation.addressGeocodeError")),
          latitude: yup
            .number()
            .typeError(t("validation.latitudeOutOfRange"))
            .min(-90, t("validation.latitudeOutOfRange"))
            .max(90, t("validation.latitudeOutOfRange"))
            .typeError(t("validation.latitudeOutOfRange"))
            .required(t("validation.required")),
          longitude: yup
            .number()
            .typeError(t("validation.longitudeOutOfRange"))
            .min(-180, t("validation.longitudeOutOfRange"))
            .max(180, t("validation.longitudeOutOfRange"))
            .typeError(t("validation.longitudeOutOfRange"))
            .required(t("validation.required")),
        },
        [["latitude", "longitude"]]
      ),
    [t]
  );

  const initialValues = location ?? { address: "" };

  return (
    <HvDialog
      buttonTitle={t("buttons.close")}
      classes={{ paper: classes.paper }}
      open={isOpen}
      onClose={onCloseHandler}
      fullWidth
      maxWidth="md"
    >
      <Formik
        initialValues={initialValues as LocationFormData}
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
      >
        {({ handleSubmit, isValid }) => (
          <form onSubmit={handleSubmit} noValidate>
            <HvDialogTitle className={classes.dialogTitle}>
              {t("title")}
            </HvDialogTitle>
            <HvDialogContent className={classes.dialogContent}>
              <Location marker={marker} />
            </HvDialogContent>
            <HvDialogActions>
              <HvButton category="ghost" type="submit" disabled={!isValid}>
                {t("buttons.save")}
              </HvButton>
              <HvButton category="ghost" onClick={onCancelHandler}>
                {t("buttons.cancel")}
              </HvButton>
            </HvDialogActions>
          </form>
        )}
      </Formik>
    </HvDialog>
  );
};

export default SelectLocationDialog;
